import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface RealtimeSyncCallbacks<T> {
  onInsert: (record: T) => void;
  onUpdate: (record: T) => void;
  onDelete: (oldRecord: { id: string }) => void;
}

interface RealtimeSyncOptions<T> extends RealtimeSyncCallbacks<T> {
  channelName: string;
  table: string;
  filter: string;
  enabled: boolean;
}

export function useRealtimeSync<T extends { id: string }>(options: RealtimeSyncOptions<T>) {
  // Use refs for callbacks to avoid re-subscribing when handlers change
  const callbacksRef = useRef<RealtimeSyncCallbacks<T>>(options);
  callbacksRef.current = options;

  useEffect(() => {
    if (!options.enabled) return;

    const channel = supabase
      .channel(options.channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: options.table,
          filter: options.filter,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            callbacksRef.current.onInsert(payload.new as T);
          } else if (payload.eventType === "UPDATE") {
            callbacksRef.current.onUpdate(payload.new as T);
          } else if (payload.eventType === "DELETE") {
            callbacksRef.current.onDelete(payload.old as { id: string });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [options.enabled, options.channelName, options.table, options.filter]);
}
